import { zodResolver } from "@hookform/resolvers/zod";
import { newPasswordSchema } from "@station/contracts";
import { Alert, Button, FormField, Input } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/auth-layout.js";
import { api } from "../lib/api.js";
import { applyFieldErrors, errorMessage } from "../lib/errors.js";

export const Route = createFileRoute("/parola-sifirla")({
  validateSearch: z.object({ token: z.string().optional() }),
  component: ResetPasswordPage,
});

export const passwordPairSchema = z
  .object({ newPassword: newPasswordSchema, confirmPassword: z.string() })
  .refine((value) => value.newPassword === value.confirmPassword, {
    path: ["confirmPassword"],
    message: "Parolalar eşleşmiyor",
  });
type PasswordPair = z.infer<typeof passwordPairSchema>;

function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<PasswordPair>({
    resolver: zodResolver(passwordPairSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const submit = form.handleSubmit(async ({ newPassword }) => {
    setFormError(null);
    try {
      await api.POST("/auth/password/reset", { body: { token: token ?? "", newPassword } });
      setDone(true);
    } catch (error) {
      const unmatched = applyFieldErrors(error, form.setError, ["newPassword"]);
      if (unmatched.length > 0 || !form.formState.errors.newPassword) setFormError(errorMessage(error));
    }
  });

  const backToLogin = (
    <Link to="/giris" className="text-primary hover:underline">
      {t("auth.backToLogin")}
    </Link>
  );
  if (!token) {
    return (
      <AuthLayout title={t("auth.reset.title")} footer={backToLogin}>
        <Alert tone="danger">{t("auth.reset.missingToken")}</Alert>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title={t("auth.reset.title")} description={t("auth.reset.description")} footer={backToLogin}>
      {done ? (
        <Alert tone="success" title={t("auth.reset.doneTitle")}>
          {t("auth.reset.doneDescription")}
        </Alert>
      ) : (
        <form onSubmit={(event) => void submit(event)} className="grid gap-4" noValidate>
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          <FormField
            label={t("auth.newPassword")}
            description={t("auth.passwordHint")}
            error={form.formState.errors.newPassword?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...form.register("newPassword")}
                type="password"
                autoComplete="new-password"
                autoFocus
              />
            )}
          </FormField>
          <FormField
            label={t("auth.confirmPassword")}
            error={form.formState.errors.confirmPassword?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...form.register("confirmPassword")}
                type="password"
                autoComplete="new-password"
              />
            )}
          </FormField>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t("auth.reset.submit")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
